// views/Admin/Bonuses/components/BonusForm/BonusForm.tsx
import { useState, useEffect, useCallback, type FormEvent, type ChangeEvent, type DragEvent } from 'react';
import Button from '../../../../../components/common/Button/Button';
import Loading from '../../../../../components/common/Loading/Loading';
import Editor from './Editor';
import DaySelector from './components/DaySelector/DaySelector';
import { Company, Game, BonusType, Risk, SpecialMark } from '../../../../../Entities';
import { notify } from '../../../../../components/common/Toast/toastBridge';
import 'react-quill/dist/quill.snow.css';
import styles from './BonusForm.module.css';

type Action = 'add' | 'edit' | 'duplicate';

interface BonusFormProps {
  bonus: any;
  onBonusAdd: (data: any) => Promise<void>;
  onBonusUpdate: (data: any) => Promise<void>;
  onCancel: () => void;
  action: Action;
  games?: any;
}

const AVAILABLE_ICONS = ['📝', '🆔', '💳', '✅', '💰', '🎮', '🎯', '📱', '📧', '🔐', '📋', '⏰', '🎁', '💸', '👑', '✨', '📞'];

function formatDateForDatetimeLocal(unixTimestamp?: number): string {
  const ts = unixTimestamp || Date.now() / 1000;
  const date = new Date(ts * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
}

function parseDatetimeLocalToUnix(datetimeLocalStr: string): number {
  return Math.floor(new Date(datetimeLocalStr).getTime() / 1000);
}

const BonusForm = ({ bonus, onBonusAdd, onBonusUpdate, onCancel, action }: BonusFormProps) => {
  const now = Math.floor(Date.now() / 1000);
  const [processing, setProcessing] = useState(false);
  const [loadedBonus, setLoadedBonus] = useState(false);
  const [currentSteps, setCurrentSteps] = useState<any[]>([]);
  const [currentGames, setCurrentGames] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({
    id: null,
    name: '',
    company: '',
    bonus_type_name: 'live',
    expected_value: '',
    description: '',
    risk_rate: 'free',
    special_mark: '',
    start_timestamp: formatDateForDatetimeLocal(now),
    end_timestamp: formatDateForDatetimeLocal(now + 60 * 60 * 3),
    visibility_start_timestamp: formatDateForDatetimeLocal(now),
    visibility_end_timestamp: formatDateForDatetimeLocal(now + 60 * 60 * 3),
    renewing_days: [],
    visible: true,
    games: [],
    needed_capital: '',
    url_bonus_conditions: '',
    url_link: '',
    step_list: [],
  });
  const [newStepForm, setNewStepForm] = useState<any>({ title: '', subtitle: '', description: '', icon: '📝' });

  const reassemblyBonus = useCallback(async () => {
    try {
      await bonus.getSteps();
      setFormData({
        id: action === 'edit' ? bonus.id : null,
        name: bonus.name,
        company: bonus.company,
        bonus_type_name: bonus.bonus_type_name,
        expected_value: bonus.expected_value,
        description: bonus.description,
        risk_rate: bonus.risk_rate,
        needed_capital: bonus.needed_capital,
        special_mark: bonus.special_mark,
        start_timestamp: formatDateForDatetimeLocal(bonus.start_timestamp),
        end_timestamp: formatDateForDatetimeLocal(bonus.end_timestamp),
        visibility_start_timestamp: formatDateForDatetimeLocal(bonus.visibility_start_timestamp),
        visibility_end_timestamp: formatDateForDatetimeLocal(bonus.visibility_end_timestamp),
        renewing_days: bonus.renewing_days,
        visible: bonus.visible,
        games: bonus.games,
        url_bonus_conditions: bonus.url_bonus_conditions,
        url_link: bonus.url_link,
        step_list: bonus.step_list,
      });
      setCurrentSteps(bonus.step_list);
      setCurrentGames(bonus.games);
      setLoadedBonus(true);
    } catch (error) {
      console.log(error);
      notify.error('Nastala chyba s načítáním kroků bonusu');
      onCancel();
    }
  }, [bonus, action, onCancel]);

  useEffect(() => {
    if (!bonus) return;
    reassemblyBonus();
  }, [bonus, reassemblyBonus]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      const data = { ...formData };
      data.start_timestamp = parseDatetimeLocalToUnix(data.start_timestamp);
      data.end_timestamp = parseDatetimeLocalToUnix(data.end_timestamp);
      data.visibility_start_timestamp = parseDatetimeLocalToUnix(data.visibility_start_timestamp);
      data.visibility_end_timestamp = parseDatetimeLocalToUnix(data.visibility_end_timestamp);
      data.step_list = currentSteps;
      data.games = currentGames;

      if (action === 'add' || action === 'duplicate') {
        await onBonusAdd(data);
      } else if (action === 'edit') {
        await onBonusUpdate(data);
      } else {
        throw new Error('unspecified action');
      }
    } catch {
      /* errors surfaced by the parent handlers */
    } finally {
      setProcessing(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === 'checkbox' ? (prev[name] === value ? null : value) : value,
    }));
  };

  const addCustomStep = () => {
    if (newStepForm.title && newStepForm.subtitle && newStepForm.description) {
      const newStep = { ...newStepForm, id: `custom-${Date.now()}`, order: currentSteps.length, category: 'custom' };
      setCurrentSteps((prev) => [...prev, newStep]);
      setNewStepForm({ title: '', subtitle: '', description: '', icon: '📝' });
    }
  };

  const removeStep = (stepId: any) => setCurrentSteps((prev) => prev.filter((step) => step.id !== stepId));
  const removeGame = (gameId: any) => setCurrentGames((prev) => prev.filter((g) => g.id !== gameId));

  const handleStepDragStart = (e: DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleStepDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleStepDrop = (e: DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (dragIndex === dropIndex) return;
    const items = Array.from(currentSteps);
    const [reordered] = items.splice(dragIndex, 1);
    items.splice(dropIndex, 0, reordered);
    setCurrentSteps(items.map((item, index) => ({ ...item, order: index })));
  };

  if (bonus && !loadedBonus) return <Loading />;

  return (
    <div className={styles.bonusForm}>
      <form>
        <div className={styles.section}>
          <h3>📋 Základní informace</h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="name">Název bonusu *</label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="form-input" required />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="bonus_type_name">Typ bonusu *</label>
              <select id="bonus_type_name" name="bonus_type_name" value={formData.bonus_type_name} onChange={handleChange} className="form-input" required>
                {BonusType.values.map((bt) => (
                  <option key={bt.id} value={bt.name}>
                    {bt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="company">Společnost *</label>
              <select
                id="company"
                name="company"
                value={formData.company?.name || ''}
                onChange={(e) => {
                  const selectedCompany = Company.values.find((c) => c.name === e.target.value);
                  setFormData((prev: any) => ({ ...prev, company: selectedCompany }));
                }}
                className="form-input"
                required
              >
                <option value="">Vyberte společnost</option>
                {Company.values.map((company) => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="expected_value">Očekávaný zisk *</label>
              <input type="number" id="expected_value" name="expected_value" value={formData.expected_value} onChange={handleChange} className="form-input" required />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="needed_capital">Potřebný kapitál (Kč)*</label>
              <input type="number" id="needed_capital" name="needed_capital" value={formData.needed_capital} onChange={handleChange} className="form-input" required />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="risk_rate">Úroveň rizika *</label>
              <select id="risk_rate" name="risk_rate" value={formData.risk_rate} onChange={handleChange} className="form-input" required>
                {Risk.values.map((risk) => (
                  <option key={risk.id} value={risk.rate}>
                    {risk.icon} {risk.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">Detailní popis *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="form-input"
              rows={4}
              placeholder="Detailní popis bonusu, podmínek a výhod..."
              required
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3>🎯 Označení</h3>
          <div className={styles.badgesSection}>
            <h4>🏷️ Speciální označení</h4>
            <div className={styles.badgeCheckboxes}>
              {SpecialMark.values.map((s_m) => (
                <label key={s_m.id} className={styles.badgeCheckbox}>
                  <input type="checkbox" name="special_mark" value={s_m.name} checked={formData.special_mark === s_m.name} onChange={handleChange} />
                  <span style={{ backgroundColor: s_m.color }} className={styles.badge}>
                    {s_m.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h3>⏰ Časové nastavení</h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="start_timestamp">Začátek platnosti bonusu *</label>
              <input type="datetime-local" id="start_timestamp" name="start_timestamp" value={formData.start_timestamp} onChange={handleChange} className="form-input" required />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="end_timestamp">Konec platnosti bonusu *</label>
              <input type="datetime-local" id="end_timestamp" name="end_timestamp" value={formData.end_timestamp} onChange={handleChange} className="form-input" required />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="visibility_start_timestamp">Začátek zobrazení uživatelům</label>
              <input type="datetime-local" id="visibility_start_timestamp" name="visibility_start_timestamp" value={formData.visibility_start_timestamp} onChange={handleChange} className="form-input" />
              <small>Ponechte prázdné pro okamžité zobrazení</small>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="visibility_end_timestamp">Konec zobrazení uživatelům</label>
              <input type="datetime-local" id="visibility_end_timestamp" name="visibility_end_timestamp" value={formData.visibility_end_timestamp} onChange={handleChange} className="form-input" />
              <small>Ponechte prázdné pro zobrazení do konce platnosti</small>
            </div>
          </div>

          <DaySelector renewingDays={formData.renewing_days} onChange={(updatedDays) => setFormData((prev: any) => ({ ...prev, renewing_days: updatedDays }))} />
        </div>

        <div className={styles.section}>
          <h3>🎰 Nastavení pro Casino</h3>
          <div className={styles.gamesSelection}>
            <h4>🎮 Doporučené hry</h4>
            <div className={styles.formGroup}>
              <label htmlFor="game">Hra</label>
              <select
                id="game"
                name="game"
                onChange={(e) => {
                  const selectedGame = Game.values.find(
                    (g) => g.id?.toString() === e.target.value && !currentGames.some((game) => game.id === g.id),
                  );
                  if (selectedGame) setCurrentGames((prev) => [...prev, selectedGame]);
                }}
                className="form-input"
              >
                <option value="">Vyberte hru</option>
                {Game.values.map((game) => (
                  <option key={game.id} value={game.id}>
                    {game.name}
                  </option>
                ))}
              </select>
              {currentGames.map((game, idx) => (
                <div key={idx} className={styles.stepItem}>
                  <div className={styles.stepItemContent}>
                    <div className={styles.stepItemTitle}>{game.name}</div>
                  </div>
                  <button type="button" onClick={() => removeGame(game.id)} className={styles.removeButton}>
                    Odstranit
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h3>📋 Kroky k získání bonusu</h3>

          <div>
            <h4>➕ Přidat vlastní krok:</h4>
            <div className={styles.customStepForm}>
              <div className={styles.customStepFormItem}>
                <select value={newStepForm.icon} onChange={(e) => setNewStepForm((prev: any) => ({ ...prev, icon: e.target.value }))}>
                  {AVAILABLE_ICONS.map((icon) => (
                    <option key={icon} value={icon}>
                      {icon}
                    </option>
                  ))}
                </select>
                <input type="text" placeholder="Název kroku" value={newStepForm.title} onChange={(e) => setNewStepForm((prev: any) => ({ ...prev, title: e.target.value }))} className={styles.formInput} />
                <input type="text" placeholder="Podnadpis" value={newStepForm.subtitle} onChange={(e) => setNewStepForm((prev: any) => ({ ...prev, subtitle: e.target.value }))} className={styles.formInput} />
              </div>
              <div className={styles.customStepFormQuill}>
                <div>
                  <Editor value={newStepForm.description} onChange={(val: string) => setNewStepForm((prev: any) => ({ ...prev, description: val }))} />
                </div>
              </div>
              <div className={styles.customStepFormItem}>
                <button
                  type="button"
                  onClick={addCustomStep}
                  className={styles.addButton}
                  disabled={!newStepForm.title || !newStepForm.subtitle || !newStepForm.description}
                >
                  Přidat
                </button>
              </div>
            </div>
          </div>

          {currentSteps.length > 0 && (
            <div>
              <h4>📋 Pořadí kroků (přetáhněte pro změnu pořadí):</h4>
              <div className={styles.stepsList}>
                {currentSteps.map((step, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) => handleStepDragStart(e, index)}
                    onDragOver={handleStepDragOver}
                    onDrop={(e) => handleStepDrop(e, index)}
                    className={styles.stepItem}
                  >
                    <div className={styles.stepItemIcon}>{step.icon}</div>
                    <div className={styles.stepItemContent}>
                      <div className={styles.stepItemTitle}>
                        {index + 1}. {step.title}
                      </div>
                      <div className={styles.stepItemSubtitle}>{step.subtitle}</div>
                      <div className="ql-editor" dangerouslySetInnerHTML={{ __html: step.description }} />
                    </div>
                    <div className={styles.stepItemActions}>
                      <button type="button" onClick={() => removeStep(step.id)} className={styles.removeButton}>
                        Odstranit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNewStepForm(step);
                          removeStep(step.id);
                        }}
                        className={styles.changeButton}
                      >
                        Upravit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.section}>
          <h3>📜 Podmínky bonusu</h3>
          <div className={styles.formGroup}>
            <label htmlFor="url_bonus_conditions">Odkaz na podmínky *</label>
            <input type="url" id="url_bonus_conditions" name="url_bonus_conditions" value={formData.url_bonus_conditions} onChange={handleChange} className="form-input" placeholder="https://www.fortuna.cz/obchodni-podminky" required />
            <small>Odkaz na oficiální stránku s podmínkami bonusu</small>
          </div>
          <br />
          <div className={styles.formGroup}>
            <label htmlFor="url_link">Odkaz na bonus *</label>
            <input type="url" id="url_link" name="url_link" value={formData.url_link} onChange={handleChange} className="form-input" placeholder="https://www.fortuna.cz/obchodni-podminky" required />
            <small>Odkaz na oficiální stránku s podmínkami bonusu</small>
          </div>
        </div>

        <div className={styles.formActions}>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Zrušit
          </Button>
          <Button type="submit" variant="primary" onClick={handleSubmit} disabled={processing}>
            {action === 'edit' ? '💾 Uložit změny' : action === 'add' ? '➕ Přidat bonus' : '📄 Duplikovat'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BonusForm;
